import { useState, useEffect } from 'react';
import api from '../../api/client';
import { getRecentStores, addRecentStore, saveCredentials } from '../../services/auth';
import logo from '../../assets/logo.png';

export default function Login({ onLogin }) {
  const [storeUrl, setStoreUrl] = useState('');
  const [username, setUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recentStores, setRecentStores] = useState([]);
  const [showRecent, setShowRecent] = useState(false);

  useEffect(() => {
    setRecentStores(getRecentStores());
  }, []);

  const handleRecentSelect = (store) => {
    setStoreUrl(store.storeUrl);
    setUsername(store.username);
    setShowRecent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate URL
    const trimmedUrl = storeUrl.trim().replace(/\/+$/, '');
    if (!trimmedUrl.startsWith('https://')) {
      setError('Store URL must start with https://');
      return;
    }

    if (!username.trim() || !appPassword.trim()) {
      setError('All fields are required.');
      return;
    }

    setLoading(true);

    try {
      api.configure(trimmedUrl, username.trim(), appPassword.trim());
      const configData = await api.getConfig();

      // Success — save and notify parent
      saveCredentials({ storeUrl: trimmedUrl, username: username.trim(), appPassword: appPassword.trim() });
      addRecentStore({ storeUrl: trimmedUrl, username: username.trim() });
      onLogin(configData);
    } catch (err) {
      api.disconnect();
      if (err.status === 401) {
        setError('Invalid credentials. Check your username and application password.');
      } else if (err.message === 'Failed to fetch') {
        setError('Could not connect to store. Check the URL and try again.');
      } else {
        setError(err.message || 'Connection failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="symple-pos-wrap">
      <div className="fixed inset-0 flex items-center justify-center bg-pos-bg">
        {/* Ambient glow */}
        <div className="fixed top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(circle, #3FBF9F 0%, transparent 70%)' }} />
        <div className="fixed bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, #486FB5 0%, transparent 70%)' }} />

        <div className="w-full max-w-md mx-4">
          {/* Logo + title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src={logo} alt="SymplePOS" className="h-16" />
            </div>
            <h1 className="text-2xl font-bold text-pos-muted-fg tracking-tight">SymplePOS</h1>
            <p className="text-pos-muted-fg text-sm mt-1">Connect to your WooCommerce store</p>
          </div>

          {/* Login card */}
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl shadow-pos-float p-6 space-y-4"
          >
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Store URL */}
            <div>
              <label className="block text-sm font-medium text-pos-foreground mb-1.5">
                Store URL
              </label>
              <div className="relative">
                <input
                  type="url"
                  value={storeUrl}
                  onChange={(e) => setStoreUrl(e.target.value)}
                  onFocus={() => recentStores.length > 0 && setShowRecent(true)}
                  onBlur={() => setTimeout(() => setShowRecent(false), 200)}
                  placeholder="https://yourstore.com"
                  className="w-full px-3 py-2.5 rounded-lg border border-pos-border bg-white text-pos-foreground placeholder:text-pos-muted-fg/50 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                  required
                />
                {/* Recent stores dropdown */}
                {showRecent && recentStores.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-pos-border rounded-lg shadow-pos-float overflow-hidden">
                    <div className="px-3 py-1.5 text-xs font-medium text-pos-muted-fg uppercase tracking-wider bg-pos-bg">
                      Recent stores
                    </div>
                    {recentStores.map((store, i) => (
                      <button
                        key={i}
                        type="button"
                        onMouseDown={() => handleRecentSelect(store)}
                        className="w-full text-left px-3 py-2 hover:bg-pos-bg transition-colors"
                      >
                        <div className="text-sm text-pos-foreground">{store.storeUrl}</div>
                        <div className="text-xs text-pos-muted-fg">{store.username}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-pos-foreground mb-1.5">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                autoComplete="username"
                className="w-full px-3 py-2.5 rounded-lg border border-pos-border bg-white text-pos-foreground placeholder:text-pos-muted-fg/50 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                required
              />
            </div>

            {/* Application Password */}
            <div>
              <label className="block text-sm font-medium text-pos-foreground mb-1.5">
                Application Password
              </label>
              <input
                type="password"
                value={appPassword}
                onChange={(e) => setAppPassword(e.target.value)}
                placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                autoComplete="current-password"
                className="w-full px-3 py-2.5 rounded-lg border border-pos-border bg-white text-pos-foreground placeholder:text-pos-muted-fg/50 focus:outline-none focus:ring-2 focus:ring-pos-primary/30 focus:border-pos-primary transition-colors"
                required
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-lg bg-pos-primary text-white font-semibold hover:bg-pos-primary-dark focus:outline-none focus:ring-2 focus:ring-pos-primary/50 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                  Connecting...
                </span>
              ) : (
                'Connect'
              )}
            </button>

            {/* Help text */}
            <p className="text-xs text-pos-muted-fg text-center pt-1">
              Generate an Application Password in your WordPress admin under
              <span className="font-medium text-pos-foreground"> Users &rarr; Profile &rarr; Application Passwords</span>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
